// L-ASS.3 FRONT-J3 (E1): gerador ÚNICO do PDF do contrato assinado — usado por Baixar e Compartilhar no
// SignedContractModal e pelo "Compartilhar contrato assinado" do diálogo da entrega.
// Envio novo (contract_content) => texto congelado + evidências (inclusive ramo presencial do M1).
// Envio antigo (sem contract_content) => getContractPdfProps (dados atuais), sem evidências.
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { formatDocument } from '@/lib/utils';
import { generatePdfFileName } from '@/utils/fileNameUtils';
import { generateContractPDF, type ContractDocumentProps } from '@/utils/pdfGenerator';
import { buildContractPdfProps } from '@/lib/rentalContract';

export interface SignedContractSignatureRow {
  id: string;
  customer_name: string;
  customer_document: string;
  customer_email: string | null;
  signed_at: string;
  signature_data: string;
  contract_title: string | null;
  contract_content: string | null;
  contract_summary: Record<string, unknown> | null;
  content_hash: string | null;
  accepted_terms_at: string | null;
  signer_ip: string | null;
  channel: string | null;
  delivery_id: string | null;
  collected_by: string | null;
  signer_document_photo_url: string | null;
  // L-ASS.2-P2: evidência do código de confirmação do link (gravada por sign_contract_by_token)
  code_verified_at: string | null;
  code_channel: string | null;
  code_destination: string | null;
}

export const SIGNED_CONTRACT_SELECT =
  'id, customer_name, customer_document, customer_email, signed_at, signature_data, contract_title, contract_content, contract_summary, content_hash, accepted_terms_at, signer_ip, channel, delivery_id, collected_by, signer_document_photo_url, code_verified_at, code_channel, code_destination';

// CPF com máscara quando tiver 11 dígitos; senão, como veio
export const maskCpf = (doc?: string | null): string => {
  if (!doc) return '';
  return doc.replace(/\D/g, '').length === 11 ? formatDocument(doc, 'cpf') : doc;
};

// "dd/MM/yyyy às HH:mm:ss (horário de Brasília)" em America/Sao_Paulo via Intl (sem date-fns-tz no projeto)
export const fmtBrasilia = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} às ${get('hour')}:${get('minute')}:${get('second')} (horário de Brasília)`;
};

interface BuildSignedContractPdfArgs {
  signatureId: string;
  companyId: string;
  rentalNumber: string;
  /** Envio antigo (sem texto congelado): props do PDF com os dados atuais da locação. */
  getContractPdfProps?: () => Promise<ContractDocumentProps | null>;
}

export async function buildSignedContractPdf({
  signatureId,
  companyId,
  rentalNumber,
  getContractPdfProps,
}: BuildSignedContractPdfArgs): Promise<{ blob: Blob; fileName: string } | null> {
  const { data: raw, error } = await supabase
    .from('contract_signatures')
    .select(SIGNED_CONTRACT_SELECT)
    .eq('id', signatureId)
    .eq('company_id', companyId)
    .maybeSingle();
  if (error) throw error;
  // types.ts defasado para as colunas do SQL-G/SQL-J — cast documentado
  const sig = raw as unknown as SignedContractSignatureRow | null;
  if (!sig) return null;
  // R2: sem assinatura efetiva não há PDF assinado
  if (!sig.signed_at || !sig.signature_data) return null;

  const isFrozen = !!sig.contract_content;
  let base: ContractDocumentProps | null;
  let evidence: ContractDocumentProps['evidence'];

  if (isFrozen) {
    base = buildContractPdfProps(sig.contract_summary ?? {}, {
      title: sig.contract_title ?? '',
      content: sig.contract_content ?? '',
    });
    evidence = {
      contentHash: sig.content_hash ?? undefined,
      acceptedAt: fmtBrasilia(sig.accepted_terms_at),
      signedAt: fmtBrasilia(sig.signed_at),
      ip: sig.signer_ip ?? undefined,
      channel: sig.channel ?? undefined,
    };
    if (sig.channel === 'presencial') {
      // M1: ramo presencial — quem colheu, entrega e documento conferido; sem e-mail
      const [{ data: deliveryRow }, { data: profileRow }] = await Promise.all([
        sig.delivery_id
          ? supabase.from('deliveries').select('delivery_number').eq('id', sig.delivery_id).eq('company_id', companyId).maybeSingle()
          : Promise.resolve({ data: null }),
        sig.collected_by
          ? supabase.from('profiles').select('full_name').eq('user_id', sig.collected_by).eq('company_id', companyId).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      evidence.collectedBy = (profileRow as any)?.full_name ?? undefined;
      evidence.deliveryNumber = (deliveryRow as any)?.delivery_number ?? undefined;
      evidence.documentChecked = !!sig.signer_document_photo_url;
    } else {
      evidence.sentTo = sig.customer_email ?? undefined;
      // L-ASS.2-P2: código conferido no link — só quando a assinatura gravou a hora do código
      if (sig.code_verified_at) {
        evidence.codeVerifiedAt = fmtBrasilia(sig.code_verified_at);
        evidence.codeChannel = sig.code_channel ?? undefined;
        evidence.codeDestination = sig.code_destination ?? undefined;
      }
    }
  } else {
    base = getContractPdfProps ? await getContractPdfProps() : null;
    if (!base) return null; // o chamador (loadContractData) já mostrou o toast
    evidence = undefined;
  }

  const blob = await generateContractPDF({
    ...base,
    contractDate: format(new Date(sig.signed_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }),
    signatureData: sig.signature_data,
    signatureCustomerName: sig.customer_name,
    signatureCustomerDocument: maskCpf(sig.customer_document),
    signatureDate: sig.signed_at,
    evidence,
  });

  return { blob, fileName: generatePdfFileName(sig.customer_name, 'ContratoAssinado', rentalNumber) };
}
